import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Input, message, Space, Popconfirm, Card, Row, Col, Tag, Modal } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BankOutlined, TeamOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hospitalAdminService } from '../../services';

const Branches = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();

    const fetchBranches = async () => {
        setLoading(true);
        try {
            const res = await hospitalAdminService.getBranches();
            setBranches(res.data || []);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBranches();
    }, []);

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setEditing(record);
        form.setFieldsValue({ name: record.name, address: record.address, phone: record.phone });
        setModalOpen(true);
    };

    const handleDelete = async (id) => {
        try {
            await hospitalAdminService.deleteBranch(id);
            message.success(t('success'));
            fetchBranches();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (editing) {
                await hospitalAdminService.updateBranch(editing._id, values);
            } else {
                await hospitalAdminService.createBranch(values);
            }
            message.success(t('success'));
            setModalOpen(false);
            fetchBranches();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const columns = [
        { title: t('branchName') || 'Branch Name', dataIndex: 'name', key: 'name' },
        { title: t('address') || 'Address', dataIndex: 'address', key: 'address' },
        { title: t('phoneNumber'), dataIndex: 'phone', key: 'phone' },
        {
            title: t('staff') || 'Staff',
            key: 'staff',
            render: (_, r) => (
                <Space size={4} wrap>
                    <Tag color="blue">{t('doctors') || 'Doctors'}: {r.staffCounts?.doctor || 0}</Tag>
                    <Tag color="cyan">{t('secretaries') || 'Sec'}: {r.staffCounts?.secretary || 0}</Tag>
                    <Tag color="geekblue">{t('lab') || 'Lab'}: {r.staffCounts?.lab || 0}</Tag>
                    <Tag color="purple">{t('pharmacy') || 'Pharmacy'}: {r.staffCounts?.pharmacy || 0}</Tag>
                    <Tag color="gold">{t('optician')}: {r.staffCounts?.optician || 0}</Tag>
                </Space>
            )
        },
        {
            title: t('actions') || 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button type="primary" icon={<TeamOutlined />} onClick={() => navigate(`/hospitaladmin/branch/${record._id}`)}>
                        {t('manageStaff') || 'Manage Staff'}
                    </Button>
                    <Button icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    <Popconfirm title={t('confirmDelete') || 'Delete?'} onConfirm={() => handleDelete(record._id)}>
                        <Button icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className="page-container">
            <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
                <Col><h1 style={{ margin: 0 }}><BankOutlined /> {t('branches') || 'Branches'}</h1></Col>
                <Col>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        {t('createBranch') || 'Create Branch'}
                    </Button>
                </Col>
            </Row>

            <Card>
                <Table
                    columns={columns}
                    dataSource={branches}
                    rowKey="_id"
                    loading={loading}
                    scroll={{ x: 800 }}
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            <Modal
                title={editing ? (t('editBranch') || 'Edit Branch') : (t('createBranch') || 'Create Branch')}
                open={modalOpen}
                onOk={handleSubmit}
                onCancel={() => setModalOpen(false)}
                okText={t('save') || 'Save'}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label={t('branchName') || 'Branch Name'} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="address" label={t('address') || 'Address'}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="phone" label={t('phoneNumber')}>
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Branches;
